import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import {
  Crown,
  Users,
  MessageSquare,
  FolderOpen,
  Calendar,
  CreditCard,
  AlertTriangle,
} from "lucide-react";

const SubscriptionManagement = () => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const navigate = useNavigate();

  const currencies = [
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  ];

  const fetchSubscriptionData = async () => {
    try {
      const [subRes, usageRes] = await Promise.all([
        api.get("/subscriptions/current"),
        api.get("/subscriptions/usage"),
      ]);

      if (subRes.data.success) {
        setSubscription(subRes.data.subscription);
      }
      if (usageRes.data.success) {
        setUsage(usageRes.data.usage);
      }

      // Don't redirect - handle all states in this component
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Don't redirect on errors - let user see the page and request a plan
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel your subscription? This will take effect at the end of your current billing period."
      )
    ) {
      return;
    }

    setCancelling(true);
    try {
      const res = await api.post("/subscriptions/cancel");
      if (res.data.success) {
        setSubscription(res.data.subscription);
        alert(
          "Subscription cancelled successfully. You'll retain access until the end of your billing period."
        );
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find((c) => c.code === currencyCode);
    return currency ? currency.symbol : "$";
  };

  const formatPrice = (price, currency) => {
    return `${getCurrencySymbol(currency)}${price}`;
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return "text-red-600 bg-red-100";
    if (percentage >= 75) return "text-orange-600 bg-orange-100";
    return "text-green-600 bg-green-100";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handlePlanRequest = () => {
    navigate("/subscription-plans");
  };

  // Handle different subscription states
  const getSubscriptionStatusInfo = () => {
    if (!subscription) {
      return {
        status: "none",
        title: "No Active Subscription",
        message:
          "You don't have an active subscription yet. Choose a plan to get started.",
        cardClass: "border-gray-300 bg-gray-50",
        textClass: "text-gray-700",
        buttonText: "Choose a Plan",
        showButton: true,
      };
    }

    const now = new Date();
    const endDate = new Date(subscription.currentPeriodEnd);
    const isExpired = now > endDate;

    switch (subscription.approvalStatus) {
      case "pending":
        return {
          status: "pending",
          title: "Subscription Request Pending",
          message:
            "Your subscription request is pending admin approval. You will be notified once it's approved.",
          cardClass: "border-yellow-400 bg-yellow-50",
          textClass: "text-yellow-800",
          buttonText: "View Plans",
          showButton: true,
        };

      case "approved":
        if (isExpired) {
          return {
            status: "expired",
            title: "Subscription Expired",
            message:
              "Your subscription has expired. Please renew to continue using our services.",
            cardClass: "border-red-400 bg-red-50",
            textClass: "text-red-800",
            buttonText: "Renew Subscription",
            showButton: true,
          };
        } else {
          return {
            status: "active",
            title: "Active Subscription",
            message: "Your subscription is active and running smoothly.",
            cardClass: "border-green-400 bg-green-50",
            textClass: "text-green-800",
            buttonText: "Upgrade Plan",
            showButton: true,
          };
        }

      case "rejected":
        return {
          status: "rejected",
          title: "Subscription Request Rejected",
          message:
            "Your subscription request was rejected. Please contact support or try with a different plan.",
          cardClass: "border-red-400 bg-red-50",
          textClass: "text-red-800",
          buttonText: "Choose New Plan",
          showButton: true,
        };

      default:
        return {
          status: "none",
          title: "No Active Subscription",
          message:
            "You don't have an active subscription yet. Choose a plan to get started.",
          cardClass: "border-gray-300 bg-gray-50",
          textClass: "text-gray-700",
          buttonText: "Choose a Plan",
          showButton: true,
        };
    }
  };

  const statusInfo = getSubscriptionStatusInfo();

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={true}
        size="lg"
        text="Loading subscription details..."
      />
    );
  }

  // Handle no subscription state with improved UI
  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <PageHeader
            title="Subscription Management"
            subtitle="Get started with a subscription plan"
          />

          {/* No Subscription Card */}
          <div
            className={`bg-white rounded-lg shadow-sm p-8 text-center border-2 ${statusInfo.cardClass}`}
          >
            <Crown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className={`text-2xl font-bold mb-3 ${statusInfo.textClass}`}>
              {statusInfo.title}
            </h2>
            <p className={`mb-6 ${statusInfo.textClass}`}>
              {statusInfo.message}
            </p>
            <Button variant="primary" size="lg" onClick={handlePlanRequest}>
              {statusInfo.buttonText}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // For subscriptions that exist, show comprehensive status info
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <PageHeader
          title="Subscription Management"
          subtitle="Manage your subscription and monitor usage"
        />

        {/* Subscription Status Card */}
        <div
          className={`bg-white rounded-lg shadow-sm p-6 mb-6 border-2 ${statusInfo.cardClass}`}
        >
          {statusInfo.status === "pending" && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-600 mx-auto mb-4"></div>
              <h2 className={`text-2xl font-bold mb-3 ${statusInfo.textClass}`}>
                {statusInfo.title}
              </h2>
              <p className={`mb-6 ${statusInfo.textClass}`}>
                {statusInfo.message}
              </p>
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Plan Details</span>
                </div>
                <p className="text-yellow-700 text-sm mt-1">
                  Plan: {subscription.subscriptionPlan.name} -{" "}
                  {formatPrice(
                    subscription.subscriptionPlan.price,
                    subscription.subscriptionPlan.currency
                  )}
                  /{subscription.subscriptionPlan.interval}
                </p>
                <p className="text-yellow-700 text-sm">
                  Requested: {formatDate(subscription.createdAt)}
                </p>
              </div>
              <button
                onClick={handlePlanRequest}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg transition font-medium"
              >
                {statusInfo.buttonText}
              </button>
            </div>
          )}

          {statusInfo.status === "rejected" && (
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className={`text-2xl font-bold mb-3 ${statusInfo.textClass}`}>
                {statusInfo.title}
              </h2>
              <p className={`mb-6 ${statusInfo.textClass}`}>
                {statusInfo.message}
              </p>
              <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Rejected Plan</span>
                </div>
                <p className="text-red-700 text-sm mt-1">
                  Plan: {subscription.subscriptionPlan.name} -{" "}
                  {formatPrice(
                    subscription.subscriptionPlan.price,
                    subscription.subscriptionPlan.currency
                  )}
                  /{subscription.subscriptionPlan.interval}
                </p>
                <p className="text-red-700 text-sm">
                  Rejected on:{" "}
                  {subscription.updatedAt
                    ? formatDate(subscription.updatedAt)
                    : "Recently"}
                </p>
              </div>
              <button
                onClick={handlePlanRequest}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition font-medium"
              >
                {statusInfo.buttonText}
              </button>
            </div>
          )}

          {(statusInfo.status === "active" ||
            statusInfo.status === "expired") && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-blue-600" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {subscription.subscriptionPlan.name} Plan
                    </h2>
                    <p className="text-gray-600">
                      {formatPrice(
                        subscription.subscriptionPlan.price,
                        subscription.subscriptionPlan.currency
                      )}
                      /{subscription.subscriptionPlan.interval}
                    </p>
                  </div>
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    subscription.cancelAtPeriodEnd
                      ? "bg-red-100 text-red-800"
                      : statusInfo.status === "active"
                      ? "bg-green-100 text-green-800"
                      : statusInfo.status === "expired"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {subscription.cancelAtPeriodEnd
                    ? "Canceling"
                    : statusInfo.status === "expired"
                    ? "Expired"
                    : statusInfo.status.charAt(0).toUpperCase() +
                      statusInfo.status.slice(1)}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Started: {formatDate(subscription.currentPeriodStart)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {statusInfo.status === "expired" ? "Expired on" : "Renews"}:{" "}
                    {formatDate(subscription.currentPeriodEnd)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {statusInfo.status === "expired" ? "Was" : "Next billing"}:{" "}
                    {formatPrice(
                      subscription.subscriptionPlan.price,
                      subscription.subscriptionPlan.currency
                    )}
                  </span>
                </div>
              </div>

              {(subscription.status === "canceled" ||
                subscription.cancelAtPeriodEnd) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Subscription Cancelled</span>
                  </div>
                  <p className="text-red-600 text-sm mt-1">
                    Your subscription will end on{" "}
                    {formatDate(subscription.currentPeriodEnd)}. You can still
                    use all features until then.
                  </p>
                </div>
              )}

              {statusInfo.status === "expired" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Subscription Expired</span>
                  </div>
                  <p className="text-red-600 text-sm mt-1">
                    Your subscription expired on{" "}
                    {formatDate(subscription.currentPeriodEnd)}. You no longer
                    have access to premium features.
                  </p>
                  <div className="mt-3">
                    <button
                      onClick={() => navigate("/subscription-plans")}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Renew Subscription
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Usage Statistics - Only show for approved subscriptions */}
        {subscription.approvalStatus === "approved" && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Usage Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  label: "Customers",
                  used: usage?.customers || 0,
                  limit: subscription.subscriptionPlan.maxCustomers,
                  icon: Users,
                  color: "blue",
                },
                {
                  label: "Groups",
                  used: usage?.groups || 0,
                  limit: subscription.subscriptionPlan.maxGroups,
                  icon: FolderOpen,
                  color: "green",
                },
                {
                  label: "Messages (Current Plan)",
                  used: usage?.messagesThisPlan || 0,
                  limit: subscription.subscriptionPlan.maxMessages,
                  icon: MessageSquare,
                  color: "purple",
                },
              ].map((stat) => {
                const Icon = stat.icon;
                const percentage = getUsagePercentage(stat.used, stat.limit);
                const isUnlimited = stat.limit === -1;

                return (
                  <div key={stat.label} className="text-center">
                    <div
                      className={`w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center ${
                        isUnlimited
                          ? "bg-gray-100"
                          : getUsageColor(percentage).split(" ")[1]
                      }`}
                    >
                      <Icon
                        className={`w-8 h-8 ${
                          isUnlimited
                            ? "text-gray-500"
                            : getUsageColor(percentage).split(" ")[0]
                        }`}
                      />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {stat.label}
                    </h4>
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {stat.used}
                      {isUnlimited ? "" : `/${stat.limit}`}
                    </div>
                    {!isUnlimited && (
                      <div className="text-sm text-gray-600">
                        {percentage.toFixed(1)}% used
                      </div>
                    )}
                    {isUnlimited && (
                      <div className="text-sm text-gray-500">Unlimited</div>
                    )}
                    {!isUnlimited && percentage >= 90 && (
                      <div className="text-xs text-red-600 mt-1 font-medium">
                        ⚠️ Near limit
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Manage Subscription
          </h3>
          <div className="flex flex-wrap gap-4">
            {statusInfo.status === "pending" && (
              <div className="text-center w-full">
                <p className="text-gray-600 mb-4">
                  Your subscription request is being reviewed by our team. We'll
                  notify you once it's approved.
                </p>
                <Button variant="primary" onClick={handlePlanRequest}>
                  View Other Plans
                </Button>
              </div>
            )}

            {statusInfo.status === "rejected" && (
              <div className="text-center w-full">
                <p className="text-gray-600 mb-4">
                  Your subscription request was not approved. Please choose a
                  different plan or contact support.
                </p>
                <div className="flex gap-4 justify-center">
                  <Button variant="primary" onClick={handlePlanRequest}>
                    Choose New Plan
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      (window.location.href = "mailto:support@yourcompany.com")
                    }
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            )}

            {statusInfo.status === "expired" && (
              <div className="text-center w-full">
                <p className="text-gray-600 mb-4">
                  Your subscription has expired. Renew now to continue accessing
                  all features.
                </p>
                <Button variant="danger" onClick={handlePlanRequest}>
                  Renew Subscription
                </Button>
              </div>
            )}

            {statusInfo.status === "active" && (
              <>
                <Button variant="primary" onClick={handlePlanRequest}>
                  Change Plan
                </Button>
                {!subscription.cancelAtPeriodEnd && (
                  <Button
                    variant="danger"
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                  >
                    {cancelling ? "Cancelling..." : "Cancel Subscription"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
